import { z } from 'zod';

/**
 * Wire schemas for the Hierarchy tab's deal overlay: the aggregate `deal_buckets` that ride on
 * `GET /api/flow/accounts/:accountId/hierarchy`, and the per-account deal list behind
 * `GET /api/flow/accounts/:accountId/hierarchy/deals` (fetched only when a tooltip opens).
 * Both read `salesforce_opportunity`; all amounts are USD. Schema is the source of truth,
 * TS types are `z.infer` only.
 */

/** Hard cap on aggregate buckets for one hierarchy (query asks for cap + 1 as a truncation sentinel). */
export const DEAL_BUCKET_CAP = 2000;
/** Deals returned for one account's tooltip (query asks for cap + 1 as a truncation sentinel). */
export const HIERARCHY_DEALS_CAP = 100;

export const DEAL_STATUSES = ['open', 'won', 'lost', 'disqualified'] as const;
export const DealStatusSchema = z.enum(DEAL_STATUSES);
export type DealStatus = z.infer<typeof DealStatusSchema>;

/** The Salesforce stage a deal carries when it was closed without being worked (not a "loss"). */
export const DISQUALIFIED_STAGE = 'Disqualified';

/**
 * The overlay's 4-way status: won (`is_won`), else closed splits on the stage into disqualified
 * vs lost, else open. The SQL `CASE` in `flow-account-hierarchy.ts` encodes the same rule.
 */
export function dealStatusOf(deal: { is_won: boolean | null; is_closed: boolean | null; stage: string | null }): DealStatus {
  if (deal.is_won === true) return 'won';
  if (deal.is_closed === true) return deal.stage === DISQUALIFIED_STAGE ? 'disqualified' : 'lost';
  return 'open';
}

/** The single largest deal in a bucket (by amount), for the node card's primary-deal line. */
export const DealRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  amount: z.number().nullable(),
  arr: z.number().nullable(),
});
export type DealRef = z.infer<typeof DealRefSchema>;

/**
 * One account x status x pipeline x deal type x close month group. `pipeline` is the
 * Opportunity record type name (null = no / unmirrored record type); close year/month are null
 * only for an undated deal.
 */
export const DealBucketSchema = z.object({
  /** Salesforce account id, the same id space as the hierarchy nodes. */
  account_id: z.string(),
  status: DealStatusSchema,
  pipeline: z.string().nullable(),
  deal_type: z.string().nullable(),
  close_year: z.number().nullable(),
  close_month: z.number().nullable(),
  deal_count: z.number(),
  amount_sum: z.number(),
  arr_sum: z.number(),
  top_deal: DealRefSchema.nullable(),
});
export type DealBucket = z.infer<typeof DealBucketSchema>;

export const HierarchyDealSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: DealStatusSchema,
  stage: z.string().nullable(),
  pipeline: z.string().nullable(),
  deal_type: z.string().nullable(),
  amount: z.number().nullable(),
  arr: z.number().nullable(),
  /** `YYYY-MM-DD`, or null when undated. */
  close_date: z.string().nullable(),
  close_year: z.number().nullable(),
  close_month: z.number().nullable(),
  owner_name: z.string().nullable(),
  loss_reason: z.string().nullable(),
  disqualification_reason: z.string().nullable(),
});
export type HierarchyDeal = z.infer<typeof HierarchyDealSchema>;

export const AccountHierarchyDealsResponseSchema = z.object({
  account_id: z.string(),
  deals: z.array(HierarchyDealSchema),
  /** True when more than `HIERARCHY_DEALS_CAP` deals exist for the account. */
  truncated: z.boolean(),
});
export type AccountHierarchyDealsResponse = z.infer<typeof AccountHierarchyDealsResponseSchema>;
